// File-validation utilities for ProofFileUpload (IOPS-5300).
// FE-side checks intentionally limited to the obvious (extension/MIME, byte
// cap, filename length); pixel-dimension validation is left to the backend.

import {
  RetailRateRequestProofType,
  retailRateRequestProofTypeFromJSON,
} from '@classpass/protobuf-definitions/lib/web/models/partners/retail_rate_requests';

export const PROOF_ITEM_STATUS = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  SUCCESS: 'success',
  ERROR: 'error',
};

export const PROOF_MAX_FILE_BYTES = 10 * 1024 * 1024;
export const PROOF_MAX_FILENAME_CHARS = 50;
export const PROOF_FILENAME_DISPLAY_MAX = 20;

// Mirrors the backend allowlist (partners-service RetailRateProofSanitizationController):
// jpg, jpeg, png, webp, pdf. Anything else is rejected server-side on sanitization.
export const PROOF_ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
export const PROOF_ALLOWED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

export const PROOF_VALIDATION_ERROR = {
  UNSUPPORTED_TYPE: 'UNSUPPORTED_TYPE',
  TOO_LARGE: 'TOO_LARGE',
  FILENAME_TOO_LONG: 'FILENAME_TOO_LONG',
};

export const getFileExtension = filename => {
  if (!filename) return '';
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1 || lastDot === filename.length - 1) return '';
  return filename.slice(lastDot + 1).toLowerCase();
};

export const sanitizeFilename = name => {
  if (!name) return '';
  // eslint-disable-next-line no-control-regex
  let cleaned = name.replace(/[\x00-\x1f\x7f]/g, '');
  cleaned = cleaned.replace(/[/\\]/g, '_');
  // Collapse a double-extension to its last segment when both are recognized
  // extensions, defending against "invoice.pdf.html" style spoofed names.
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    const last = parts[parts.length - 1].toLowerCase();
    const secondLast = parts[parts.length - 2].toLowerCase();
    if (PROOF_ALLOWED_EXTENSIONS.includes(secondLast) && PROOF_ALLOWED_EXTENSIONS.includes(last)) {
      cleaned = `${parts.slice(0, -2).join('.')}.${last}`;
    }
  }
  return cleaned.trim();
};

export const truncateFilename = (name, max = PROOF_FILENAME_DISPLAY_MAX) => {
  if (!name) return '';
  if (name.length <= max) return name;
  const ext = getFileExtension(name);
  if (!ext) return `${name.slice(0, Math.max(0, max - 1))}…`;
  const dotExt = `.${ext}`;
  const headLen = Math.max(1, max - dotExt.length - 1);
  return `${name.slice(0, headLen)}…${dotExt}`;
};

export const validateFile = file => {
  if (!file) return { valid: false, error: PROOF_VALIDATION_ERROR.UNSUPPORTED_TYPE };

  const extension = getFileExtension(file.name);
  const extensionAllowed = PROOF_ALLOWED_EXTENSIONS.includes(extension);
  // Some browsers leave file.type blank; fall back to the extension check then.
  const mimeAllowed = !file.type || PROOF_ALLOWED_MIME_TYPES.includes(file.type);

  if (!extensionAllowed || !mimeAllowed) {
    return { valid: false, error: PROOF_VALIDATION_ERROR.UNSUPPORTED_TYPE };
  }

  if (file.size > PROOF_MAX_FILE_BYTES) {
    return { valid: false, error: PROOF_VALIDATION_ERROR.TOO_LARGE };
  }

  if (file.name.length > PROOF_MAX_FILENAME_CHARS) {
    return { valid: false, error: PROOF_VALIDATION_ERROR.FILENAME_TOO_LONG };
  }

  return { valid: true };
};

export const isImageMime = mimeType => /^image\//.test(mimeType || '');

export const errorMessageFor = (errorCode, intlMessages) => {
  switch (errorCode) {
    case PROOF_VALIDATION_ERROR.UNSUPPORTED_TYPE:
      return intlMessages.proofUnsupportedFileType;
    case PROOF_VALIDATION_ERROR.TOO_LARGE:
      return intlMessages.proofFileTooLarge;
    case PROOF_VALIDATION_ERROR.FILENAME_TOO_LONG:
      return intlMessages.proofFilenameTooLong;
    default:
      return intlMessages.proofUploadFailed;
  }
};

// Lets the caller distinguish CORS/network failures (the S3 bucket rejecting a
// cross-origin PUT) from genuine S3 4xx/5xx responses.
export class ProofUploadNetworkError extends Error {
  constructor(cause) {
    super('Network or CORS error contacting S3');
    this.name = 'ProofUploadNetworkError';
    this.cause = cause;
  }
}

// Uses fetch (not httpClient) so our auth/CSRF interceptors don't add headers
// that S3 will reject.
export const putFileToPresignedUrl = async (uploadUrl, file) => {
  let response;
  try {
    response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
  } catch (error) {
    throw new ProofUploadNetworkError(error);
  }
  if (!response.ok) {
    throw new Error(`S3 upload failed with status ${response.status}`);
  }
};

// Interprets the latest proof row into a prefill action, or null when there's
// nothing to prefill. The file item is returned without a local `id`; the
// caller assigns one (makeLocalId) so this stays pure.
export const getPrefillFromLatestProof = latest => {
  if (!latest) return null;

  const proofType = retailRateRequestProofTypeFromJSON(latest.type);

  if (proofType === RetailRateRequestProofType.RETAIL_RATE_REQUEST_PROOF_TYPE_URL) {
    // Legacy "not listed online" rows stored the sentinel as a URL proof; treat
    // them as "no link" so the sentinel never lands in the visible link field.
    if (!latest.url || latest.url.includes('not_listed_online')) return null;
    return { kind: 'url', url: latest.url };
  }

  if (proofType === RetailRateRequestProofType.RETAIL_RATE_REQUEST_PROOF_TYPE_FILE) {
    return {
      kind: 'file',
      item: {
        file: null,
        originalFileName: latest.original_file_name || 'proof',
        mimeType: '',
        status: PROOF_ITEM_STATUS.SUCCESS,
        downloadUrl: latest.url || null,
        // The backend id lets the partner re-submit this proof without
        // re-uploading; legacy rows without an id stay display-only.
        fileProofId: latest.id ?? null,
        isPrefilled: true,
      },
    };
  }

  return null;
};
